import { Button } from "@workith/ui";
import { useId, useState } from "react";
import type {
  CollaborationRecoveryStatus,
  CollaborationRecoverySummary,
} from "./collaborationRecovery";

interface CollaborationRecoveryBannerProps {
  summary: CollaborationRecoverySummary;
  status: CollaborationRecoveryStatus;
  onCopy: () => void;
  onDownload: () => void;
  onDismiss: () => void;
}

const STATUS_TEXT: Readonly<Record<CollaborationRecoveryStatus, string>> = {
  ready: "",
  copying: "클립보드에 복사하는 중입니다.",
  downloading: "파일을 준비하는 중입니다.",
  copied: "Markdown을 클립보드에 복사했습니다. 복구 내용은 이 탭의 메모리에서 지웠습니다.",
  downloaded: "Markdown 파일을 내려받았습니다. 복구 내용은 이 탭의 메모리에서 지웠습니다.",
  copyFailed: "클립보드 복사에 실패했습니다. 파일 내려받기를 사용해 주세요.",
  downloadFailed: "파일 내려받기에 실패했습니다. 클립보드 복사를 사용해 주세요.",
};

const BUSY_STATUSES: ReadonlySet<CollaborationRecoveryStatus> = new Set(["copying", "downloading"]);
const DONE_STATUSES: ReadonlySet<CollaborationRecoveryStatus> = new Set(["copied", "downloaded"]);

/**
 * The current-tab recovery offer (R12-R13).
 *
 * Accessibility decisions worth keeping:
 * - It appears without taking focus. A revocation is not a moment to move the
 *   caret out of the text the user was typing, and the controls are reachable
 *   by Tab from where focus already is.
 * - A partial export is the alert. It is mounted once with static text, so a
 *   screen reader hears "N sections are missing" exactly once, while the buttons
 *   stay outside any live region - controls inside one get re-announced on every
 *   state change.
 * - Export progress and outcome go through a polite `<output>` region, so
 *   "copied" or "failed" never interrupts the assertive disclosure.
 * - Nothing exports on its own: copy and download are each one explicit click,
 *   and dismissing warns before it drops content that exists nowhere else.
 */
export default function CollaborationRecoveryBanner({
  summary,
  status,
  onCopy,
  onDownload,
  onDismiss,
}: CollaborationRecoveryBannerProps) {
  const titleId = useId();
  const [isConfirmingDismiss, setIsConfirmingDismiss] = useState(false);
  const isBusy = BUSY_STATUSES.has(status);
  const isDone = DONE_STATUSES.has(status);
  const statusText = STATUS_TEXT[status];

  return (
    <section
      aria-labelledby={titleId}
      className="flex flex-col gap-2 rounded-md border border-amber-500/60 bg-amber-50 p-3 text-xs dark:bg-amber-950/40"
    >
      <h2 id={titleId} className="text-sm font-medium">
        이 탭에만 남은 변경사항 복구
      </h2>

      {/* Only a partial export raises its own alert. A complete one adds
          nothing the terminal announcement did not already say, and a second
          assertive region would just talk over the first. */}
      {summary.isPartial ? (
        <p role="alert">
          {`부분 내보내기입니다. 이 탭에 불러온 ${summary.includedCount}개 영역만 포함하며, 불러오지 않은 섹션 ${summary.omittedCount}개는 빠져 있습니다.`}
        </p>
      ) : (
        <p>{`이 탭에 불러온 ${summary.includedCount}개 영역을 모두 포함합니다.`}</p>
      )}

      <p className="text-muted-foreground">
        서버가 이 탭의 변경사항을 거부했습니다. 복구는 현재 탭에서만 가능하며 새로고침하거나 탭을
        닫으면 사라집니다. 내보낸 사본은 사용자가 직접 관리하며 Workith가 삭제하거나 회수할 수
        없습니다.
      </p>

      <output aria-live="polite" className="text-muted-foreground">
        {statusText}
      </output>

      {!isDone && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            aria-label="복구 내용을 Markdown으로 복사"
            disabled={isBusy}
            onClick={onCopy}
          >
            Markdown 복사
          </Button>
          <Button
            variant="secondary"
            size="sm"
            aria-label="복구 내용을 Markdown 파일로 내려받기"
            disabled={isBusy}
            onClick={onDownload}
          >
            Markdown 내려받기
          </Button>
          {isConfirmingDismiss ? (
            <>
              <span>내보내지 않고 닫으면 이 변경사항은 복구할 수 없습니다.</span>
              <Button
                variant="ghost"
                size="sm"
                aria-label="복구하지 않고 닫기 확인"
                onClick={onDismiss}
              >
                그래도 닫기
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label="복구 닫기 취소"
                onClick={() => setIsConfirmingDismiss(false)}
              >
                취소
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              aria-label="복구 알림 닫기"
              disabled={isBusy}
              onClick={() => setIsConfirmingDismiss(true)}
            >
              닫기
            </Button>
          )}
        </div>
      )}

      {isDone && (
        <div>
          <Button variant="ghost" size="sm" aria-label="복구 알림 닫기" onClick={onDismiss}>
            닫기
          </Button>
        </div>
      )}
    </section>
  );
}
